import { useEffect, useRef, useState } from "react";
import { ApiError, api, errMsg, postNote, type Account, type NotePreview, type TradingNote } from "../api";
import { fmtDay, formatMoney } from "../lib/money";
import { useMe } from "../session";
import { Modal } from "./Modal";
import { Btn, Field, Input, Notice, Select } from "./ui";

const brl = (m: { decimal: string }) => formatMoney(m.decimal, "BRL");
const sign = (m: { decimal: string }) => (m.decimal.startsWith("-") ? "neg" : "pos");

/** Importar nota de corretagem: escolhe a conta da corretora e o PDF, confere o que foi lido e só então grava. */
export function ImportNoteDialog({ entityId, onClose, onDone }: { entityId: string; onClose: () => void; onDone: (n: TradingNote) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [needPassword, setNeedPassword] = useState(false);
  const [pv, setPv] = useState<NotePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api<Account[]>("GET", `/api/${realm}/accounts?entityId=${entityId}&status=ativa`).then((a) => {
      const inv = a.filter((x) => x.type === "investimento" && x.currency === "BRL");
      setAccounts(inv); if (inv.length === 1) setAccountId(inv[0]!.id);
    }).catch((e) => setErr(errMsg(e)));
  }, [realm, entityId]);

  async function read() {
    if (!file) return;
    setErr(null); setBusy(true);
    try { setPv(await postNote<NotePreview>(`/api/${realm}/trading-notes/preview?entityId=${entityId}`, file, password || undefined)); setNeedPassword(false); }
    catch (e) {
      if (e instanceof ApiError && (e.code === "note_password_required" || e.code === "note_password_wrong")) setNeedPassword(true);
      setPv(null); setErr(errMsg(e));
    } finally { setBusy(false); }
  }
  async function confirm() {
    if (!file || !pv || !accountId) return;
    setErr(null); setBusy(true);
    try {
      const n = await postNote<TradingNote>(`/api/${realm}/trading-notes?entityId=${entityId}&accountId=${accountId}&name=${encodeURIComponent(file.name)}&expectedNet=${pv.net.cents}`, file, password || undefined);
      onDone(n);
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  }

  return (
    <Modal title="Importar nota de corretagem" onClose={onClose} wide>
      <div data-import-note>
        {err && <Notice kind="error">{err}</Notice>}
        {accounts.length === 0 && <Notice>Cadastre uma conta do tipo <b>Investimento</b> (em reais) em Contas Financeiras para receber o resultado das notas da corretora.</Notice>}
        {!pv && (
          <>
            <Field label="Conta da corretora" required>
              <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} name="noteAccount">
                <option value="">Escolha…</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <div className="dropzone">
              <div>{file ? <b>{file.name}</b> : "Escolha o PDF da nota de corretagem (BM&F / futuros)."}</div>
              <input ref={input} type="file" accept="application/pdf,.pdf" name="noteFile" data-note-file onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPv(null); setErr(null); }} />
            </div>
            {(needPassword || password) && <Field label="Senha do PDF (geralmente os 5 primeiros dígitos do CPF)"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" name="notePassword" /></Field>}
            <div className="modal-actions">
              <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
              <button type="button" className="btn primary" disabled={!file || !accountId || busy} onClick={() => void read()} data-note-read>{busy ? "Lendo…" : "Ler nota"}</button>
            </div>
          </>
        )}
        {pv && (
          <>
            <p className="muted small">Confira o que foi lido. Nada foi gravado ainda.</p>
            <div className="inv-box" data-note-preview>
              <div className="inv-row"><span>Corretora</span><b>{pv.broker.name}</b></div>
              <div className="inv-row"><span>Nota / data do pregão</span><span>{pv.noteNumber} · {fmtDay(pv.tradeDate)}</span></div>
              <div className="inv-row"><span>Resultado do dia</span><b className={sign(pv.gross)}>{brl(pv.gross)}</b></div>
              <div className="inv-row"><span>Corretagem e taxas</span><span>− {brl(pv.costs)}</span></div>
              <div className="inv-row"><span>IRRF day trade (retido)</span><span>− {brl(pv.irrf)}</span></div>
              <div className="inv-row big"><span>Líquido da nota (na conta)</span><b className={sign(pv.net)} data-note-net>{brl(pv.net)}</b></div>
            </div>
            <div className="card table-card"><table className="note-trades" aria-label="Operações da nota">
              <thead><tr><th>C/V</th><th>Contrato</th><th>Venc.</th><th className="num">Qtde</th><th className="num">Preço</th><th>Tipo</th><th className="num">Valor</th></tr></thead>
              <tbody>{pv.trades.map((t, i) => (
                <tr key={i}><td>{t.side}</td><td>{t.instrument}</td><td>{fmtDay(t.maturity)}</td><td className="num">{t.quantity}</td><td className="num tabular">{t.price.replace(".", ",")}</td>
                  <td>{t.tradeType === "day_trade" ? "Day trade" : "Normal"}</td><td className={`num tabular ${sign(t.amount)}`}>{brl(t.amount)}</td></tr>))}</tbody>
            </table></div>
            {pv.warnings.map((w) => <p key={w} className="small muted">• {w}</p>)}
            {pv.duplicate && <Notice kind="error">{pv.duplicate.reason} Exclua a nota anterior antes de importar de novo.</Notice>}
            <p className="small muted">Ao confirmar, serão lançados na conta (já confirmados): o resultado (receita ou despesa), a corretagem/taxas e o IRRF retido. O PDF fica guardado com a nota.</p>
            <div className="modal-actions">
              <Btn variant="ghost" onClick={() => { setPv(null); setErr(null); }}>Voltar</Btn>
              <button type="button" className="btn primary" disabled={busy || !!pv.duplicate} onClick={() => void confirm()} data-note-confirm>{busy ? "Gravando…" : "Confirmar importação"}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
